import { SimpleEntityGridColumn } from "src/app/components/grid/SimpleEntityGrid";
import { toAbsoluteUrl } from "src/_metronic/helpers/AssetHelpers";
import SVG from "react-inlinesvg";
import moment from "moment";
import { filterParams } from "../../grid/GridHelpers";
import { TActionData } from "./useSiteAnalyzeOffSiteGridColumns";
import { startCase } from "lodash";
import { KTSVG } from "src/_metronic/helpers";
import { OrderOffSiteGrid } from "src/app/types/orders/offSite";
import ActionButton from "../../ActionButton/ActionButton";

export default function offSiteGridRankColumns(
    formatMessage: any,
    actions: TActionData
): Array<SimpleEntityGridColumn> {
    return [
        {
            field: "id",
            filter: "agSetColumnFilter",
            cellClass: "number-cell",
            minWidth: 60,
            maxWidth: 60,
            hide: true,
        },
        {
            field: "publishedAt",
            filter: "agDateColumnFilter",
            comparator: (date1, date2) => {
                const timeStamp1 = new Date(
                    moment(date1, "DD/MM/YYYY").format("YYYY-MM-DD")
                ).getTime();
                const timeStamp2 = new Date(
                    moment(date2, "DD/MM/YYYY").format("YYYY-MM-DD")
                ).getTime();
                return timeStamp1 - timeStamp2;
            },
            headerName: formatMessage({ id: "content.grid.col.publishedAt" }),
            valueGetter: ({ data }: any) => moment(data.publishedAt).format("DD/MM/YY"),
            filterParams,
            minWidth: 120,
            maxWidth: 120,
        },
        {
            field: "url",
            headerName: formatMessage({ id: "content.grid.col.url" }),
        },
        {
            field: "nl",
            headerName: formatMessage({ id: "content.grid.col.nl" }),
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            minWidth: 65,
            maxWidth: 65,
        },
        {
            field: "silo",
            headerName: formatMessage({ id: "content.grid.col.silos" }),
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            minWidth: 65,
            maxWidth: 65,
        },
        {
            field: "writingType",
            headerName: formatMessage({ id: "urls.grid.col.writingType" }),
            valueGetter: ({ data }: any) =>
                formatMessage({
                    id: `content.grid.col.writingType.${data.writingType}`,
                }),
            filter: "agSetColumnFilter",
            minWidth: 120,
            maxWidth: 120,
        },
        {
            field: "wordCount",
            headerName: formatMessage({ id: "content.grid.col.wordCount" }),
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            minWidth: 65,
            maxWidth: 65,
        },
        {
            field: "keyword",
            autoHeight: true,
            headerName: formatMessage({ id: "content.grid.col.keyword" }),
            valueGetter: ({ data }: any) => {
                return data?.keywords;
            },
            cellRenderer: ({ data }: any) => (
                <div className="thematic-react-tag-wrapper gap-2 pb-2 pt-2">
                    {data?.keywords?.map((category: string, index: number) => (
                        <div key={index} className="thematic-react-tag">
                            {category}
                        </div>
                    ))}
                </div>
            ),
        },
        {
            field: "indexed",
            minWidth: 80,
            maxWidth: 80,
            headerName: formatMessage({ id: "content.grid.col.indexed" }),
            filter: "agSetColumnFilter",
            valueGetter: ({ data }: any) => {
                return data?.indexed;
            },
            cellRenderer: (params: { data: any }) => {
                const { data } = params;
                if (data?.indexed === 0) {
                    return (
                        <SVG
                            src={toAbsoluteUrl("/media/icons/duotune/general/gen034.svg")}
                            className="svg-icon-danger"
                        />
                    );
                }
                if (data?.indexed === 1) {
                    return (
                        <SVG
                            src={toAbsoluteUrl("/media/icons/duotune/general/gen037.svg")}
                            className="svg-icon-valid"
                        />
                    );
                }
                if (data?.indexed === 2) {
                    return (
                        <SVG
                            src={toAbsoluteUrl("/media/icons/duotune/general/gen046.svg")}
                            className="svg-icon-warning"
                        />
                    );
                }
            },
            filterParams: {
                cellRenderer: (params: { value: any }) => {
                    const value = params.value;
                    if (value === "0") {
                        return (
                            <SVG
                                src={toAbsoluteUrl("/media/icons/duotune/general/gen034.svg")}
                                className="svg-icon-danger"
                            />
                        );
                    }
                    if (value === "1") {
                        return (
                            <SVG
                                src={toAbsoluteUrl("/media/icons/duotune/general/gen037.svg")}
                                className="svg-icon-valid"
                            />
                        );
                    }
                    if (value === "2") {
                        return (
                            <SVG
                                src={toAbsoluteUrl("/media/icons/duotune/general/gen046.svg")}
                                className="svg-icon-warning"
                            />
                        );
                    }
                    return value;
                },
            },
        },
        {
            field: "indexedAt",
            headerName: formatMessage({ id: "content.grid.col.indexedAt" }),
            valueGetter: ({ data }) =>
                data?.indexedAt ? moment(data.indexedAt).format("DD/MM/YY") : "N/A",
            filter: "agDateColumnFilter",
            comparator: (date1, date2) => {
                if (date1 === "N/A" || date2 === "N/A") return 0;
                const timeStamp1 = new Date(
                    moment(date1, "DD/MM/YYYY").format("YYYY-MM-DD")
                ).getTime();
                const timeStamp2 = new Date(
                    moment(date2, "DD/MM/YYYY").format("YYYY-MM-DD")
                ).getTime();
                return timeStamp1 - timeStamp2;
            },
            filterParams: {
                suppressAndOrCondition: true,
                filterOptions: ["equals", "greaterThanOrEqual", "lessThanOrEqual"],
            },
            minWidth: 100,
            maxWidth: 100,
        },
        {
            field: "kd",
            headerName: formatMessage({ id: "content.grid.col.kd" }),
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            minWidth: 65,
            maxWidth: 65,
        },
        {
            field: "volume",
            headerName: formatMessage({ id: "content.grid.col.volume" }),
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            minWidth: 65,
            maxWidth: 65,
        },
        {
            field: "actions",
            minWidth: actions.width,
            maxWidth: actions.width,
            cellRenderer: ({ data }: { data: OrderOffSiteGrid }) => {
                return (
                    <>
                        {actions.actions.map((action, index) => (
                            <ActionButton
                                key={index}
                                actions={actions.actives}
                                name={action.name}
                                data={data}
                            >
                                <a
                                    onClick={() => actions.handleActionModals(action.name, data)}
                                    title={startCase(
                                        formatMessage({ id: `common.action.${action.name}` })
                                    )}
                                    className="inline-action"
                                >
                                    <KTSVG path={action.icon} className="svg-icon-2" />
                                </a>
                            </ActionButton>
                        ))}
                    </>
                );
            },
        },
    ];
}
